/**
 * Shared RGBA colour math. Hex is a typing format only; persisted documents store `Rgba`.
 */

import { clamp } from './math.ts';

export type Rgba = {
  r: number;
  g: number;
  b: number;
  a: number;
};

export type Hsv = {
  h: number;
  s: number;
  v: number;
};

export function clampByte(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.min(255, Math.round(value)));
}

export function clampRgba(value: Rgba): Rgba {
  return {
    r: clampByte(value.r),
    g: clampByte(value.g),
    b: clampByte(value.b),
    a: clamp(Number.isFinite(value.a) ? value.a : 0, 0, 1),
  };
}

export function isRgba(value: unknown): value is Rgba {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const record = value as Record<string, unknown>;

  return (
    typeof record.r === 'number' &&
    Number.isFinite(record.r) &&
    typeof record.g === 'number' &&
    Number.isFinite(record.g) &&
    typeof record.b === 'number' &&
    Number.isFinite(record.b) &&
    typeof record.a === 'number' &&
    Number.isFinite(record.a)
  );
}

export function cloneRgba(value: Rgba): Rgba {
  return { r: value.r, g: value.g, b: value.b, a: value.a };
}

export function rgbaEquals(left: Rgba, right: Rgba): boolean {
  const a = clampRgba(left);
  const b = clampRgba(right);

  return a.r === b.r && a.g === b.g && a.b === b.b && Math.round(a.a * 255) === Math.round(b.a * 255);
}

export function rgb24(rgba: Rgba): number {
  const color = clampRgba(rgba);

  return (color.r << 16) | (color.g << 8) | color.b;
}

export function rgbaFromRgb24(rgb: number, a = 1): Rgba {
  const packed = Number.isFinite(rgb) ? Math.max(0, Math.min(0xffffff, Math.round(rgb))) : 0;

  return clampRgba({
    r: (packed >> 16) & 0xff,
    g: (packed >> 8) & 0xff,
    b: packed & 0xff,
    a,
  });
}

/** `#rrggbb` from an RGBA (alpha dropped) or a packed 24-bit integer. */
export function toCssHex(color: Rgba | number): string {
  const packed = typeof color === 'number' ? color & 0xffffff : rgb24(color);

  return `#${packed.toString(16).padStart(6, '0').toLowerCase()}`;
}

export function toCssHexa(rgba: Rgba): string {
  const color = clampRgba(rgba);
  const alpha = Math.round(color.a * 255)
    .toString(16)
    .padStart(2, '0');

  return `${toCssHex(color)}${alpha}`;
}

/**
 * Accepts `#rgb`, `#rrggbb`, `#rrggbbaa`, with or without `#`.
 * 3- and 6-digit forms keep `currentA`.
 */
export function parseHex(input: string, currentA = 1): Rgba | null {
  const digits = input.trim().replace(/^#/, '');

  if (!/^[0-9a-fA-F]+$/.test(digits)) {
    return null;
  }

  if (digits.length === 3) {
    return clampRgba({
      r: Number.parseInt(digits[0]! + digits[0], 16),
      g: Number.parseInt(digits[1]! + digits[1], 16),
      b: Number.parseInt(digits[2]! + digits[2], 16),
      a: currentA,
    });
  }

  if (digits.length === 6) {
    return clampRgba({
      r: Number.parseInt(digits.slice(0, 2), 16),
      g: Number.parseInt(digits.slice(2, 4), 16),
      b: Number.parseInt(digits.slice(4, 6), 16),
      a: currentA,
    });
  }

  if (digits.length === 8) {
    return clampRgba({
      r: Number.parseInt(digits.slice(0, 2), 16),
      g: Number.parseInt(digits.slice(2, 4), 16),
      b: Number.parseInt(digits.slice(4, 6), 16),
      a: Number.parseInt(digits.slice(6, 8), 16) / 255,
    });
  }

  return null;
}

export function hsvFromRgb(r: number, g: number, b: number): Hsv {
  const red = clampByte(r) / 255;
  const green = clampByte(g) / 255;
  const blue = clampByte(b) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;

  if (delta !== 0) {
    if (max === red) {
      hue = ((green - blue) / delta + 6) % 6;
    } else if (max === green) {
      hue = (blue - red) / delta + 2;
    } else {
      hue = (red - green) / delta + 4;
    }

    hue /= 6;
  }

  return { h: hue, s: max === 0 ? 0 : delta / max, v: max };
}

export function rgbFromHsv(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const hue = clamp(h, 0, 1);
  const sat = clamp(s, 0, 1);
  const val = clamp(v, 0, 1);
  const sector = Math.floor(hue * 6);
  const frac = hue * 6 - sector;
  const p = val * (1 - sat);
  const q = val * (1 - frac * sat);
  const t = val * (1 - (1 - frac) * sat);
  let red = 0;
  let green = 0;
  let blue = 0;

  switch (sector % 6) {
    case 0:
      red = val;
      green = t;
      blue = p;
      break;
    case 1:
      red = q;
      green = val;
      blue = p;
      break;
    case 2:
      red = p;
      green = val;
      blue = t;
      break;
    case 3:
      red = p;
      green = q;
      blue = val;
      break;
    case 4:
      red = t;
      green = p;
      blue = val;
      break;
    default:
      red = val;
      green = p;
      blue = q;
      break;
  }

  return {
    r: Math.round(red * 255),
    g: Math.round(green * 255),
    b: Math.round(blue * 255),
  };
}

export function hsvFromRgba(rgba: Rgba): Hsv {
  const color = clampRgba(rgba);

  return hsvFromRgb(color.r, color.g, color.b);
}

export function rgbaFromHsv(hsv: Hsv, a: number): Rgba {
  return clampRgba({ ...rgbFromHsv(hsv.h, hsv.s, hsv.v), a });
}

export function mixRgb24(from: number, to: number, amount: number): number {
  const t = clamp(amount, 0, 1);
  const origin = rgbaFromRgb24(from, 1);
  const target = rgbaFromRgb24(to, 1);

  return rgb24({
    r: origin.r + (target.r - origin.r) * t,
    g: origin.g + (target.g - origin.g) * t,
    b: origin.b + (target.b - origin.b) * t,
    a: 1,
  });
}

/** Outline a fill by mixing toward black — close to the old moss/pine palette pairs. */
export function strokeFromFill(rgb: number): number {
  return mixRgb24(rgb, 0x000000, 0.32);
}
